import { Link } from "react-router-dom";
import styled from "styled-components";

import { motion } from "./motionUtils";

// ============================================================================
// LAYOUT COMPONENTS
// ============================================================================

export const AuthContainer = styled.div`
  background: #eef1f7;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
  overflow: hidden;
`;

export const AuthCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 40px;
  max-width: 488px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 32px 24px;
    width: calc(100% - 40px);
    max-width: 488px;
    margin: 20px auto;
  }
`;

// Split layout for SignUp
export const SplitAuthCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  width: 100%;
  max-width: 976px;
  overflow: hidden;
  position: relative;
  z-index: 1;

  @media (max-width: 1024px) {
    flex-direction: column;
    max-width: 488px;
  }

  @media (max-width: 768px) {
    margin: 15px;
    max-width: calc(100% - 10px);
  }
`;

export const LeftSection = styled.div`
  background: ${({ theme }) => theme.colors.main.white};
  width: 390px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  @media (max-width: 1024px) {
    width: 100%;
  }
`;

export const LeftContent = styled.div`
  padding: ${({ padding }) => padding || "30px"};
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${({ gap }) => gap || "0"};
  flex: 1;

  @media (max-width: 1024px) {
    padding: 24px 18px;
  }
`;

export const RightSection = styled.div`
  background: ${({ theme }) => theme.colors.main.white};
  width: 606px;
  display: flex;
  flex-direction: column;
  position: relative;
  border-radius: 0 16px 16px 0;

  @media (max-width: 992px) {
    display: none;
  }
`;

export const RightContent = styled.div`
  padding: 42px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;

  @media (max-width: 1024px) {
    padding: 32px 24px;
  }
`;

export const Divider = styled.div`
  width: 1px;
  background: ${({ theme }) => theme.colors.main.authFooterBackground};
  min-height: 100%;

  @media (max-width: 1024px) {
    display: none;
  }
`;

// ============================================================================
// HEADER COMPONENTS
// ============================================================================

export const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: ${({ marginBottom }) => marginBottom || "0"};
  justify-content: ${({ justifyContent }) => justifyContent || "center"};
  align-items: ${({ alignItems }) => alignItems || "center"};
  width: 100%;
  align-self: flex-start;
`;

export const Title = styled.h1`
  color: ${({ primary, theme, color }) =>
    color ? color : primary ? theme.colors.main.authTextPrimary : "#000e2a"};
  font-weight: ${({ fontWeight, theme }) =>
    fontWeight || theme.fontWeights.bold || 700};
  font-size: ${({ fontSize }) => fontSize || "20px"};
  line-height: ${({ lineHeight }) => lineHeight || "30px"};
  margin: ${({ margin }) => margin || "0"};
  text-align: ${({ textAlign }) => textAlign || "center"};
  width: 100%;

  @media (max-width: 768px) {
    font-size: ${({ mobileFontSize }) => mobileFontSize || "18px"};
    line-height: ${({ mobileLineHeight }) => mobileLineHeight || "30px"};
  }
`;

export const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.main.labelText};
  font-size: ${({ fontSize }) => fontSize || "14px"};
  line-height: ${({ lineHeight }) => lineHeight || "18px"};
  margin: ${({ margin }) => margin || "0 0 10px"};
  text-align: ${({ align }) => align || "center"};
  margin: ${({ margin }) => margin || "0"};

  @media (max-width: 768px) {
    font-size: ${({ mobileFontSize }) => mobileFontSize || "13px"};
    line-height: ${({ mobileLineHeight }) => mobileLineHeight || "18px"};
  }
`;

// ============================================================================
// FORM COMPONENTS
// ============================================================================

export const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ gap }) => gap || "32px"};
  margin-top: ${({ marginTop }) => marginTop || "0"};
  width: ${({ width }) => width || "400px"};
  align-items: flex-start;
  align-self: flex-start;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const InputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

export const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ gap }) => gap || "24px"};
  width: 100%;
`;

export const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
`;

export const StyledInput = styled.input`
  border: 1px solid
    ${({ hasError, hasSuccess }) =>
      hasError ? "#ff5722" : hasSuccess ? "#03ce1b" : "#999faa"};
  border-radius: ${({ hasError, hasSuccess }) =>
    hasError || hasSuccess ? "12px 12px 0 0" : "12px"};
  padding: 10px 12px;
  height: 40px;
  width: 100%;
  font-size: 14px;
  font-weight: ${({ hasError }) => (hasError ? 500 : 400)};
  line-height: 21px;
  color: #000e2a;
  background: white;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ hasError, hasSuccess }) =>
      hasError ? "#ff5722" : hasSuccess ? "#03ce1b" : "#2266ef"};
    box-shadow: ${({ hasError, hasSuccess }) =>
      hasError
        ? "0 0 0 3px rgba(255, 87, 34, 0.1)"
        : hasSuccess
        ? "0 0 0 3px rgba(3, 206, 27, 0.1)"
        : "0 0 0 3px rgba(34, 102, 239, 0.1)"};
  }

  &::placeholder {
    color: #666e7f;
    font-size: 14px;
    font-weight: 400;
    line-height: 21px;
  }

  &:disabled {
    background: #f5f5f5;
    color: #999;
    cursor: not-allowed;
  }
`;

export const PasswordInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

export const CodeInputField = styled.input`
  flex: 1;
  height: 64px;
  border: 1px solid #999faa;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
  color: #1e2223;
  background: white;
  transition: all 0.2s ease;
  text-align: center;
  outline: none;
  box-sizing: border-box;
  min-width: 0;

  &:focus {
    border-color: #2266ef;
    box-shadow: 0 0 0 3px rgba(34, 102, 239, 0.1);
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }

  @media (max-width: 576px) {
    height: 56px;
    font-size: 20px;
  }
`;

export const CodeInputContainer = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  flex-wrap: nowrap;
`;

export const CodeInputLabel = styled.p`
  margin: 0 0 8px 0;
  padding: 0;
  font-size: 14px;
  font-weight: 400;
  color: #4b4e4f;
  line-height: 21px;
  text-align: left;
`;

// ============================================================================
// BUTTON COMPONENTS
// ============================================================================
export const ButtonSection = styled.div`
  display: flex;
  flex-direction: ${({ direction }) => direction || "row"};
  gap: ${({ gap }) => gap || "12px"};
  width: ${({ width }) => width || "100%"};
  margin-top: ${({ marginTop }) => marginTop || "0"};
`;

export const PrimaryButton = styled(motion.button)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;
  gap: 8px;
  width: ${({ width }) => (width ? width : "100%")};
  height: 40px;
  background: linear-gradient(90deg, #2266ef 0%, #6c2ef2 100%);
  border-radius: 32px;
  border: none;
  flex: ${({ flex }) => (flex === "1" ? "1 0 0" : "none")};
  align-self: stretch;
  flex-grow: ${({ flex }) => (flex === "1" ? "1" : "0")};
  min-width: 0;
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 21px;
  text-align: center;
  color: #ffffff;
  cursor: pointer;
  transition: opacity 0.2s ease;
  position: relative;
  overflow: hidden;

  &:disabled {
    background: linear-gradient(90deg, #9ca3af 0%, #9ca3af 100%);
    cursor: not-allowed;
    opacity: 0.6;
  }

  &:focus {
    outline: none;
  }

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

export const BackButton = styled(motion.button)`
  background: #e9f0fe;
  border: none;
  border-radius: 32px;
  height: 40px;
  width: ${({ width }) => width || "100px"};
  color: #000e2a;
  font-weight: 700;
  font-size: 14px;
  line-height: 21px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #d0dffc;
    transform: translateY(-1px);
  }
`;

// ============================================================================
// DIVIDER COMPONENTS
// ============================================================================

export const OrDivider = styled.p`
  color: #4b4e4f;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  text-align: center;
  margin: 0;
`;

// ============================================================================
// LINK COMPONENTS
// ============================================================================

export const AuthLink = styled.a`
  color: ${({ color, theme }) => color || theme.colors.main.authBlue};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-size: ${({ fontSize }) => fontSize || "14px"};
  line-height: ${({ lineHeight }) => lineHeight || "18px"};
  margin: ${({ margin }) => margin || "0"};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    text-decoration: underline;
    transform: translateY(-1px);
  }
  &:focus {
    color: ${({ color, theme }) => color || theme.colors.main.authBlue};
  }

  @media (max-width: 768px) {
    font-size: ${({ mobileFontSize }) => mobileFontSize || "13px"};
    line-height: ${({ mobileLineHeight }) => mobileLineHeight || "16px"};
  }
`;

export const AuthNavLink = styled(Link)`
  color: ${({ color, theme }) => color || "#2266ef"};
  font-weight: ${({ theme }) => theme.fontWeights.bold || 700};
  font-size: ${({ fontSize }) => fontSize || "14px"};
  line-height: ${({ lineHeight }) => lineHeight || "21px"};
  margin: ${({ margin }) => margin || "0"};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ color, theme }) => color || "#2266ef"};
    text-decoration: underline;
    transform: translateY(-1px);
  }
  &:focus {
    color: ${({ color, theme }) => color || "#2266ef"};
  }

  @media (max-width: 768px) {
    font-size: ${({ mobileFontSize }) => mobileFontSize || "13px"};
    line-height: ${({ mobileLineHeight }) => mobileLineHeight || "16px"};
  }
`;

export const TermsLink = styled.a`
  color: #353a46;
  text-decoration: underline;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;

  &:hover {
    color: #2266ef;
  }
`;

// ============================================================================
// SECTION COMPONENTS
// ============================================================================

export const AuthSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  line-height: 21px;
  justify-content: flex-start;
  margin-top: ${({ marginTop }) => marginTop || "0"};
  width: 100%;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

export const AuthText = styled.span`
  color: ${({ color, theme }) => color || "#4b4e4f"};
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
`;

// Success and Cloudflare branding section
export const SuccessBrandingSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 0;
`;

export const SuccessIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  border-radius: 8px;
`;

export const SuccessText = styled.span`
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  color: #4b4e4f;
`;

export const CloudflareBranding = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

export const CloudflareLogo = styled.div`
  height: 26.42px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 180.727px;
`;

export const PrivacyTermsLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #4b4e4f;
`;

export const PrivacyTermsLink = styled.a`
  color: #4b4e4f;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #2266ef;
    text-decoration: underline;
  }
`;

// ============================================================================
// FOOTER COMPONENTS
// ============================================================================

export const FooterSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 16px 40px;
  gap: 10px;

  width: auto;
  height: 53px;
  margin: 0 -40px -40px -40px;

  background: linear-gradient(90deg, #edf8fe 0%, #f4ecfe 100%);
  border: 1px solid #ffffff;
  border-radius: 0 0 16px 16px;

  font-size: 14px;
  line-height: 21px;
  color: #353a46;
  font-weight: 400;
  text-align: center;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px 24px;
    margin: 0 -24px -32px -24px;
    height: auto;
    min-height: 53px;
  }
`;

// ============================================================================
// ERROR COMPONENTS
// ============================================================================

export const ErrorMessage = styled(motion.div)`
  background: #ff5722;
  color: white;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  padding: 4px 8px;
  border-radius: 0 0 8px 8px;
  margin-top: 0;
  margin-left: 0;
  text-align: left;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

export const SuccessMessage = styled(motion.div)`
  background: #03ce1b;
  color: white;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  padding: 4px 8px;
  border-radius: 0 0 8px 8px;
  margin-top: 0;
  margin-left: 0;
  text-align: left;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
`;

export const ServerErrorAlert = styled(motion.div)`
  background: ${({ theme }) => theme.colors.main.authErrorBackground};
  color: ${({ theme }) => theme.colors.main.white};
  font-size: 12.5px;
  font-weight: 500;
  line-height: 16px;
  padding: 12px 16px;
  border-radius: 8px;
  margin: 0;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export const ServerErrorIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

// ============================================================================
// UTILITY COMPONENTS
// ============================================================================

export const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

// ============================================================================
// THEME CUSTOMIZATION UTILITIES
// ============================================================================

export const createCustomAuthCard = customStyles => styled(AuthCard)`
  ${customStyles}
`;

export const createCustomButton = customStyles => styled(PrimaryButton)`
  ${customStyles}
`;

export const createCustomInput = customStyles => styled(StyledInput)`
  ${customStyles}
`;

// ============================================================================
// RESPONSIVE UTILITIES
// ============================================================================

export const mobileStyles = `
  @media (max-width: 768px) {
    padding: 32px 24px;
    margin: 20px;
    max-width: calc(100% - 10px);
  }
`;

export const tabletStyles = `
  @media (max-width: 1024px) {
    width: 100%;
    padding: 32px 24px;
  }
`;

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================

export const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

export const scaleIn = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.95 },
};

export const slideIn = {
  initial: { opacity: 0, x: -20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 20 },
};

// ============================================================================
// RIGHT SECTION COMPONENTS
// ============================================================================

export const RightSectionHeaderContainer = styled.div`
  text-align: center;
  margin-bottom: 0;
  padding: 0 24px;
`;

export const RightSectionHeaderTitle = styled.h2`
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-size: 24px;
  color: ${({ theme }) => theme.colors.main.textSecondary};
  margin: 0 0 8px 0;
  text-align: center;
`;

export const RightSectionHeaderText = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.main.labelText};
  margin: 0;
  text-align: center;
  max-width: 400px;
  margin: 0 auto;
`;

export const FreeText = styled.span`
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.main.primary};
  font-style: italic;
`;

export const ItalicText = styled.span`
  color: ${({ theme }) => theme.colors.main.textSecondary};
  font-style: italic;
`;

export const ReviewSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 12px 0;
  gap: 16px;
`;

export const RatingContainer = styled.div`
  background: rgba(255, 255, 255, 0.25);
  border: 1px solid ${({ theme }) => theme.colors.border.secondaryLight};
  border-radius: 32px;
  padding: 8px 16px 8px 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 42px;
`;

export const CompanyLogos = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const CompanyLogo = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: -8px;
  border: 1px solid white;

  &:first-child {
    background: rgba(255, 255, 255, 0.5);
  }

  &:last-child {
    background: rgba(255, 255, 255, 0.75);
  }
`;

export const RatingInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const RatingScore = styled.span`
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  font-size: 15px;
  color: ${({ theme }) => theme.colors.main.authTextPrimary};
`;

export const StarRating = styled.div`
  display: flex;
  gap: 2px;
  align-items: center;
`;

export const Star = styled.div`
  width: ${props => props.size || 20}px;
  height: ${props => props.size || 20}px;
  position: relative;
  display: inline-block;
`;

export const StarSVG = styled.svg`
  width: ${props => props.size || 20}px;
  height: ${props => props.size || 20}px;
  position: absolute;
  top: 0;
  left: 0;
`;

export const StarFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 2;
  clip-path: inset(0 ${props => `${(1 - props.fillPercentage) * 100}%`} 0 0);
`;

export const StarOutline = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

export const ReviewText = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.main.textSecondary};
  margin: 0;
  font-weight: 500;
  text-align: center;
`;

export const InfiniteBanner = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.colors.main.authFooterBackground};
  border-radius: 0 0 16px 0;
  padding: 16px 20px;
  height: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  overflow: hidden;
  position: relative;
`;

export const BannerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
`;

export const BannerLogo = styled.div`
  height: 20px;
  display: flex;
  align-items: center;
  flex-shrink: 0;

  img {
    height: 100%;
    width: auto;
    object-fit: contain;
  }
`;

export const BannerText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.main.textSecondary};
  font-weight: 500;
  text-align: center;
  margin: 0;
`;

// ============================================================================
// EXPORT ALL COMPONENTS
// ============================================================================

export default {
  // Layout
  AuthContainer,
  AuthCard,
  SplitAuthCard,
  LeftSection,
  LeftContent,
  RightSection,
  RightContent,
  Divider,

  // Header
  HeaderSection,
  Title,
  Subtitle,

  // Form
  FormSection,
  InputSection,
  InputContainer,
  InputGroup,
  StyledInput,
  PasswordInputContainer,
  PasswordToggle,
  CodeInputField,
  CodeInputContainer,
  CodeInputLabel,

  // Buttons
  PrimaryButton,
  BackButton,

  // Dividers
  OrDivider,

  // Links
  AuthLink,
  TermsLink,

  // Sections
  AuthSection,
  AuthText,

  // Footer
  FooterSection,

  // Error
  ErrorMessage,
  ServerErrorAlert,
  ServerErrorIcon,

  // Utilities
  LoadingSpinner,

  // Right Section Components
  RightSectionHeaderContainer,
  RightSectionHeaderTitle,
  RightSectionHeaderText,
  FreeText,
  ItalicText,
  ReviewSection,
  RatingContainer,
  CompanyLogos,
  CompanyLogo,
  RatingInfo,
  RatingScore,
  StarRating,
  Star,
  ReviewText,
  InfiniteBanner,
  BannerContent,
  BannerLogo,

  // Customization
  createCustomAuthCard,
  createCustomButton,
  createCustomInput,

  // Responsive
  mobileStyles,
  tabletStyles,

  // Animations
  fadeInUp,
  scaleIn,
  slideIn,
};
